import { MedusaLineItem, MedusaProduct, MedusaProductCollection } from '../generated/types/api';

const productMatchesCollection = <T extends MedusaProduct, U extends Partial<MedusaProductCollection>>(
  product: T,
  collection: U
): boolean =>
  (product.collection_id && product.collection_id === collection.id) ||
  (product.collection && product.collection?.id === collection.id) ||
  (product.collection && product.collection?.title === collection.title);

export const selectCollectionByTitle = <T extends Partial<MedusaProductCollection>>(
  collections: T[],
  collectionTitle: string
) => collections.find(collection => collection.title === collectionTitle);

export const filterProductsByCollection = <T extends MedusaProduct, U extends Partial<MedusaProductCollection>>(
  products: T[],
  collection: U
): T[] => products.filter(product => product && productMatchesCollection(product, collection));

export const filterLineItemsByCollection = <T extends MedusaLineItem, U extends Partial<MedusaProductCollection>>(
  items: T[],
  collection: U
): T[] => items?.filter(item => item.variant?.product && productMatchesCollection(item.variant?.product, collection));

export const selectProductsFromLineItems = <T extends MedusaLineItem>(items: T[]): MedusaProduct[] =>
  items?.map(item => item.variant.product);

export const selectSubscriptionItem = <T extends MedusaLineItem>(items: T[]): T =>
  items.find(item => item.variant.product.collection.title === 'Subscriptions');

export const selectMealLineItems = <T extends MedusaLineItem>(items: T[]): T[] => {
  const mealLineItems = filterLineItemsByCollection(items, { title: 'Meals' });
  return mealLineItems?.sort((a, b) => (a.metadata?.sort_order < b.metadata?.sort_order ? -1 : 1));
};

export const selectExtraLineItems = <T extends MedusaLineItem>(items: T[]): T[] =>
  filterLineItemsByCollection(items, { title: 'Extras' })?.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));

export const selectPremiumLineItems = <T extends MedusaLineItem>(items: T[]): T[] =>
  items.filter(item => !!item.variant.metadata.premium_upcharge);
